/* eslint-disable max-len */
import React, { ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Form, FormControl, FormGroup, FormLabel, Row, Col,
} from "react-bootstrap";
import { useDispatch, useSelector } from 'react-redux';
import { BsExclamationTriangleFill } from "react-icons/bs";
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import {
    calcualteDOB,
    calculatePasswordErrorClasses,
    capitalizeWord,
    formatDOB,
    formatMobileNumber,
    isPhoneValid,
    validateDOB,
    validateOnlyLettersSpace,
    validatePassword,
    validateZipCode,
} from 'app/shared/utils/helperFunctions';
import { setSignUpFormValidation, updateSignUpForm } from 'app/store/slices/authSlice';
import getBrandName from 'app/shared/utils/BrandHelper';
import { getErrorMessage } from 'app/shared/constants/ErrorMessages';
import { getLabel } from 'app/shared/constants/Literals';
import { MAX_AGE, MIN_AGE } from 'app/shared/constants/constants';
import { RootState } from '../../store';
import { ISignUpForm } from './types/SignUpForm';
import { NotificationType } from './Notifications/types/NotificationType';
import useIsSMSEnabled from '../../shared/hooks/useIsSMSEnabled';

const SignUpDetails: React.FC = () => {
    const appInsights = useAppInsightsContext();
    if (appInsights) {
        appInsights.trackPageView({
            name: "SignUpDetails",
        });
        const metricData = {
            average: 5,
            name: "React Component Engaged Time (seconds)",
            sampleCount: 1,
        };
        const additionalProperties = { "Component Name": 'SignUpDetails' };
        appInsights.trackMetric(metricData, additionalProperties);
    }

    const initialState = {
        firstName: '',
        lastName: '',
        emailAddress: '',
        memberPhone: '',
        dob: '',
        zipCode: '',
        password: '',
        confirmPassword: '',
    };

    const intPasswordErrorClasses = {
        hasPasswordError: false,
        passwordRuleClass: 'mutedText',
        minLengthPasswordRuleClass: 'mutedText',
        hasNumberPasswordRuleClass: 'mutedText',
        hasUpperCasePasswordRuleClass: 'mutedText',
    };

    const brandConfigs = useSelector((state: RootState) => state.auth.configs.brandConfigs);
    const privacyUrl = brandConfigs?.PRIVACY_POLICY_URL || "#";
    const termsUrl = brandConfigs?.TERMS_AND_CONDITIONS_URL || "#";
    const incentiveUrl = brandConfigs?.NOTICE_OF_FINANCIAL_INCENTIVE || "#";

    const { brandTitle } = getBrandName();
    const dispatch = useDispatch();
    const {
        signUpForm,
    } = useSelector((state: RootState) => state.auth);

    const isSMSEnabled = useIsSMSEnabled();
    const [signUpFormError, setSignUpFormError] = useState(initialState);
    const [passwordError, setPasswordError] = useState({
        minLength: false,
        hasNumber: false,
        hasUpperCase: false,
    });
    const [passwordErrorClasses, setPasswordErrorClasses] = useState(intPasswordErrorClasses);

    useEffect(() => {
        const requiredFields = [
            'firstName',
            'lastName',
            'emailAddress',
            'confirmPassword',
            'memberPhone',
            'dob',
            'zipCode',
        ];

        const hasPasswordError = (!passwordError.hasNumber
            || !passwordError.minLength
            || !passwordError.hasUpperCase);

        let isInvalid = Object.values(signUpFormError).some((field: string) => field)
            || requiredFields.some((field: string) => !signUpForm[field as keyof ISignUpForm])
            || hasPasswordError;

        //Enable the CTA right after the password and confirm password matches, not on focus out of input field
        isInvalid = isInvalid
        || (signUpForm.password !== "" && signUpForm.confirmPassword !== "" && (signUpForm.password !== signUpForm.confirmPassword));

        dispatch(setSignUpFormValidation(isInvalid));

        return () => {
            //reset error on unload
            dispatch(setSignUpFormValidation(false));
        };
    }, [signUpFormError, signUpForm, passwordError, dispatch]);

    useEffect(() => {
        //reset on change
        setSignUpFormError((formError) => ({
            ...formError,
            password: "",
            confirmPassword: "",
        }));

        setPasswordError(validatePassword(signUpForm.password));
    }, [signUpForm.password]);

    useEffect(() => {
        const {
          hasPasswordError,
          passwordRuleClass,
          minLengthPasswordRuleClass,
          hasNumberPasswordRuleClass,
          hasUpperCasePasswordRuleClass,
        } = calculatePasswordErrorClasses(
          signUpForm.password,
          passwordError,
          signUpFormError.password,
        );

        setPasswordErrorClasses({
          hasPasswordError,
          passwordRuleClass,
          minLengthPasswordRuleClass,
          hasNumberPasswordRuleClass,
          hasUpperCasePasswordRuleClass,
        });
      }, [signUpForm.password, signUpFormError.password, passwordError]);

    useEffect(() => {
        dispatch(updateSignUpForm({
            ...signUpForm,
            firstName: '',
            lastName: '',
            emailAddress: '',
            memberPhone: '',
            dob: '',
            password: '',
            confirmPassword: '',
            isSpecialOfferChecked: true,
            isCateringChecked: true,
            isSmsChecked: false,
        }));
        // eslint-disable-next-line
    }, []);

    const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { checked, name } = event.currentTarget;

        dispatch(updateSignUpForm({
            ...signUpForm,
            [name]: checked,
        }));
    };

    const transformAndUpdateValue = (name:string, value:string) => {
        const signUpFormData = {
            firstName: signUpForm.firstName,
            lastName: signUpForm.lastName,
            emailAddress: signUpForm.emailAddress,
            memberPhone: signUpForm.memberPhone,
            dob: signUpForm.dob,
            zipCode: signUpForm.zipCode,
            password: signUpForm.password,
            confirmPassword: signUpForm.confirmPassword,
            isSpecialOfferChecked: signUpForm.isSpecialOfferChecked,
            isCateringChecked: signUpForm.isCateringChecked,
            ...(isSMSEnabled && { isSmsChecked: signUpForm.isSmsChecked }),
        };

        let inputValue = "";

        switch (name) {
            case "memberPhone":
                inputValue = formatMobileNumber(value) || "";
                break;
            case "dob":
                inputValue = formatDOB(value) || "";
                break;
            default:
                inputValue = value;
                break;
        }

        dispatch(updateSignUpForm({
            ...signUpFormData,
            [name]: inputValue,
        }));
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        const { name, value } = event.currentTarget;
        transformAndUpdateValue(name, value);
    };

    const validateFields = (field: string) => {
        let isValid = true;
        let errorType = "";

        if (field === "firstName" && (signUpForm.firstName === "" || !signUpForm.firstName)) {
            isValid = false;
            errorType = getErrorMessage('SIGNUP', 'missingFirstName');
        }

        if (field === "firstName" && signUpForm.firstName && !validateOnlyLettersSpace(signUpForm.firstName)) {
            isValid = false;
            errorType = getErrorMessage('SIGNUP', 'invalidFirstName');
        }

        if (field === "lastName" && (signUpForm.lastName === "" || !signUpForm.lastName)) {
            isValid = false;
            errorType = getErrorMessage('SIGNUP', 'missingLastName');
        }

        if (field === "lastName" && signUpForm.lastName && !validateOnlyLettersSpace(signUpForm.lastName)) {
            isValid = false;
            errorType = getErrorMessage('SIGNUP', 'invalidLastName');
        }

        if (field === "memberPhone" && (signUpForm.memberPhone === "" || !signUpForm.memberPhone)) {
            isValid = false;
            errorType = getErrorMessage('SIGNUP', 'missingPhoneNumber');
        }

        if (signUpForm.memberPhone && field === "memberPhone" && !isPhoneValid(signUpForm.memberPhone)) {
            isValid = false;
            errorType = getErrorMessage('SIGNUP', 'invalidPhoneNumber');
        }

        if (field === "dob" && (signUpForm.dob === "" || !signUpForm.dob)) {
            isValid = false;
            errorType = getErrorMessage('SIGNUP', 'missingBirthday');
        }

        if (signUpForm.dob && field === "dob" && (!validateDOB(signUpForm.dob) || calcualteDOB(signUpForm.dob) >= MAX_AGE)) {
            isValid = false;
            errorType = getErrorMessage('SIGNUP', 'invalidBirthday');
        }

        if (signUpForm.dob && field === "dob" && validateDOB(signUpForm.dob) && calcualteDOB(signUpForm.dob) < MIN_AGE) {
            isValid = false;
            errorType = `Sorry, you need to be at least ${MIN_AGE} to use ${capitalizeWord(brandTitle)} Rewards. See you in a couple years.`;
        }

        if (field === "zipCode" && (signUpForm.zipCode === "" || !signUpForm.zipCode)) {
            isValid = false;
            errorType = getErrorMessage('SIGNUP', 'missingZipCode');
        }

        if (signUpForm.zipCode && field === "zipCode" && validateZipCode(signUpForm.zipCode) !== "Valid") {
            isValid = false;
            errorType = getErrorMessage('SIGNUP', 'invalidZipCode');
        }

        if (field === "emailAddress" && (signUpForm.emailAddress === "" || !signUpForm.emailAddress)) {
            isValid = false;
            errorType = getErrorMessage('SIGNUP', 'missingEmailAddress');
        }

        if (field === "emailAddress" && signUpForm.emailAddress) {
            const regex = /^(([^<>()[\].,;:\s@!#$%&'*/=?^{|}~"\\]+(\.[^<>()[\].,;:\s@!#$%&'*/=?^{|}~"\\]+)*)|(".+"))@(([^<>()[\].,;:\s@!#$%&'*/=?^{|}~"\\]+\.)+[^<>()[\].,;:\s@!#$%&'*/=?^{|}~"\\]{2,})$/i;
            if (!signUpForm.emailAddress || !regex.test(signUpForm.emailAddress)) {
                isValid = false;
                errorType = getErrorMessage('SIGNUP', 'invalidEmailAddress');
            }
        }

        if (field === "password" && (signUpForm.password === "" || !signUpForm.password)) {
            isValid = false;
            errorType = getErrorMessage('SIGNUP', 'missingPassword');
        }

        if (field === "password" && (!!signUpForm.password && signUpForm.password !== "")) {
            const checkPasswordError = validatePassword(signUpForm.password);

            const hasPasswordError = (!checkPasswordError.hasNumber
                || !checkPasswordError.minLength
                || !checkPasswordError.hasUpperCase);

            isValid = !hasPasswordError;
            errorType = getErrorMessage('SIGNUP', 'invalidPassword');
        }

        let confirmPasswordError = "";

        if (field === "confirmPassword" && ((signUpForm.confirmPassword === "" || !signUpForm.confirmPassword))) {
            confirmPasswordError = getErrorMessage('SIGNUP', 'missingConfirmPassword');
        } else if (signUpForm.confirmPassword !== "" && (signUpForm.confirmPassword !== signUpForm.password)) {
            confirmPasswordError = getErrorMessage('SIGNUP', 'confirmPasswordMismatch');
        }

        setSignUpFormError({
            ...signUpFormError,
            [field]: !isValid ? `${errorType}` : "",
            confirmPassword: confirmPasswordError,
        });
    };

    function trimmFields(fieldName:string) {
        const currentValue = signUpForm[fieldName as keyof typeof signUpForm] as string;
        const trimmedValue = currentValue?.trim();
        if (currentValue !== trimmedValue) {
            transformAndUpdateValue(fieldName, trimmedValue);
        }
    }

    function handleBlur(fieldName:string) {
        trimmFields(fieldName);
        validateFields(fieldName);
    }

    const createNotificationLabel = (label: string, subLabel: string | ReactNode) => (
        <p className="font-sub">
            <span className="font-main">{label}</span>
            <span>- </span>
            {subLabel}
        </p>
    );

    const getLabelByNotificationType = (type: NotificationType) => {
        switch (type) {
            case NotificationType.EMAIL:
                return createNotificationLabel(
                    getLabel('SIGNUP', 'emailNotifications'),
                    getLabel('SIGNUP', 'emailConsent'),
                );
            case NotificationType.EMAIL_CATERING:
                return createNotificationLabel(
                    getLabel('SIGNUP', 'cateringEmail'),
                    getLabel('SIGNUP', 'cateringConsent'),
                );
            case NotificationType.SMS:
                return createNotificationLabel(
                    getLabel('SIGNUP', 'smsNotifications'),
                    <>
                        {getLabel('SIGNUP', 'smsConsent')}
                        <span className="d-block legal-text mt-3">
                            {getLabel('SIGNUP', 'signUpSmsDataRates')}
                        </span>
                    </>,
                );
            default:
                return "";
        }
    };

    const [termsText = "", privaryText = ""] = getLabel('SIGNUP', 'signUpByApplying').split(":terms");
    const [privacyLeftText = "", privacyRightText = ""] = privaryText.split(":privacy");
    const [financialIncentiveText = "", financialIncentiveRightText = ""] = privacyRightText.split(":financialIncentive");

    const privacyAndTermsText = (
    <p className="mt-3 text-center font-sub f-w-100">
        {termsText}
        <Link to={termsUrl} target="_blank" style={{color: "inherit"}}>Bagel Brands Terms and Conditions</Link>
        {privacyLeftText}
        <Link to={privacyUrl} target="_blank" style={{color: "inherit"}}>Privacy Policy</Link>
        {financialIncentiveText}
        <Link to={incentiveUrl} target="_blank" style={{color: "inherit"}}>Notice of Financial Incentive</Link>
        {financialIncentiveRightText}
    </p>
    );

    return (
        <div className="form-container mt-4">
            <Form noValidate id="signup-rewards">
                <Row>
                    <Col md={{ span: 4, offset: 4 }}>
                        <FormGroup className="mb-3" controlId="formBasicFirstName">
                            <FormLabel className="font-main">
                            {getLabel('SIGNUP', 'firstName')}
                                <span className="label-required">*</span>
                            </FormLabel>
                            <FormControl
                                aria-required
                                type="text"
                                name="firstName"
                                value={signUpForm.firstName}
                                onChange={handleChange}
                                onBlur={() => handleBlur("firstName")}
                                isInvalid={(signUpFormError.firstName !== "")}
                                className="checkout-input"
                                aria-label="First Name"
                                placeholder="First Name"
                                autoFocus
                            />
                            <FormControl.Feedback type="invalid">
                                <BsExclamationTriangleFill size={16} className="errorImage" />
                                <span className="errorText font-sub">{signUpFormError.firstName}</span>
                            </FormControl.Feedback>
                        </FormGroup>
                        <FormGroup className="mb-3" controlId="formBasicLastName">
                            <FormLabel className="font-main">
                            {getLabel('SIGNUP', 'lastName')}
                                <span className="label-required">*</span>
                            </FormLabel>
                            <FormControl
                                aria-required
                                type="text"
                                name="lastName"
                                value={signUpForm.lastName}
                                onChange={handleChange}
                                onBlur={() => handleBlur("lastName")}
                                isInvalid={(signUpFormError.lastName !== "")}
                                className="checkout-input"
                                aria-label="Last Name"
                                placeholder="Last Name"
                            />
                            <FormControl.Feedback type="invalid">
                                <BsExclamationTriangleFill size={16} className="errorImage" />
                                <span className="errorText font-sub">{signUpFormError.lastName}</span>
                            </FormControl.Feedback>
                        </FormGroup>
                        <FormGroup className="mb-3" controlId="formBasicEmail">
                            <FormLabel className="font-main">
                            {getLabel('SIGNUP', 'emailAddress')}
                                <span className="label-required">*</span>
                            </FormLabel>
                            <FormControl
                                aria-required
                                type="email"
                                name="emailAddress"
                                value={signUpForm.emailAddress}
                                onChange={handleChange}
                                onBlur={() => handleBlur("emailAddress")}
                                isInvalid={(signUpFormError.emailAddress !== "")}
                                className="checkout-input"
                                aria-label="Email Address"
                                placeholder="Email Address"
                            />
                            <FormControl.Feedback type="invalid">
                                <BsExclamationTriangleFill size={16} className="errorImage" />
                                <span className="errorText font-sub">{signUpFormError.emailAddress}</span>
                            </FormControl.Feedback>
                        </FormGroup>
                        <FormGroup className="mb-3" controlId="formBasicMemberPhone">
                            <FormLabel className="font-main">
                            {getLabel('SIGNUP', 'phoneNumber')}
                                <span className="label-required">*</span>
                            </FormLabel>
                            <FormControl
                                aria-required
                                type="text"
                                name="memberPhone"
                                value={signUpForm.memberPhone}
                                onChange={handleChange}
                                onBlur={() => handleBlur("memberPhone")}
                                isInvalid={(signUpFormError.memberPhone !== "")}
                                maxLength={10}
                                aria-label="Phone Number"
                                className="checkout-input"
                                placeholder="Phone Number"
                            />
                            <FormControl.Feedback type="invalid">
                                <BsExclamationTriangleFill size={16} className="errorImage" />
                                <span className="errorText font-sub">{signUpFormError.memberPhone}</span>
                            </FormControl.Feedback>
                            <span className="font-sub">For in-bakery check-in purposes</span>
                        </FormGroup>
                        <FormGroup className="mb-3" controlId="formBasicDOB">
                            <FormLabel className="font-main">
                            {getLabel('SIGNUP', 'birthday')}
                                <span className="label-required">*</span>
                            </FormLabel>
                            <FormControl
                                aria-required
                                type="text"
                                name="dob"
                                maxLength={10}
                                value={signUpForm.dob}
                                onChange={handleChange}
                                onBlur={() => handleBlur("dob")}
                                isInvalid={(signUpFormError.dob !== "")}
                                aria-label="Birthday"
                                className="checkout-input"
                                placeholder="MM/DD/YYYY"
                            />
                            <FormControl.Feedback type="invalid">
                                <BsExclamationTriangleFill size={16} className="errorImage" />
                                <span className="errorText font-sub">{signUpFormError.dob}</span>
                            </FormControl.Feedback>
                        </FormGroup>
                        <FormGroup className="mb-3" controlId="formBasicZipCode">
                            <FormLabel className="font-main">
                            {getLabel('SIGNUP', 'zipCode')}
                                <span className="label-required">*</span>
                            </FormLabel>
                            <FormControl
                                aria-required
                                type="text"
                                name="zipCode"
                                maxLength={5}
                                value={signUpForm.zipCode ?? ''}
                                onChange={handleChange}
                                onBlur={() => handleBlur("zipCode")}
                                isInvalid={(signUpFormError.zipCode !== "")}
                                aria-label="Zip Code"
                                className="checkout-input"
                                placeholder="Zip Code"
                            />
                            <FormControl.Feedback type="invalid">
                                <BsExclamationTriangleFill size={16} className="errorImage" />
                                <span className="errorText font-sub">{signUpFormError.zipCode}</span>
                            </FormControl.Feedback>
                        </FormGroup>
                        <FormGroup className="mb-1" controlId="formBasicPassword">
                            <FormLabel className="font-main">
                            {getLabel('SIGNUP', 'password')}
                                <span className="label-required">*</span>
                            </FormLabel>
                            <FormControl
                                aria-required
                                type="password"
                                name="password"
                                value={signUpForm.password}
                                onChange={handleChange}
                                onBlur={() => validateFields("password")}
                                isInvalid={(signUpFormError.password !== "")}
                                className="checkout-input"
                                aria-label="Password"
                                placeholder="Password"
                                autoComplete="off"
                                data-testid="password"
                            />
                            {signUpFormError.password !== "" && signUpFormError.password !== "Invalid" && !passwordErrorClasses.hasPasswordError && (
                                <FormControl.Feedback type="invalid">
                                    <BsExclamationTriangleFill size={16} className="errorImage" />
                                    <span className="errorText font-sub">{signUpFormError.password}</span>
                                </FormControl.Feedback>
                            )}
                        </FormGroup>
                        <Row className="mb-3">
                            {signUpForm.password && signUpForm.password !== "" && (
                                <Col className={`font-sub ${passwordErrorClasses.passwordRuleClass}`}>
                                    <p className="mb-0 pb-0 d-flex align-center gap-2">
                                        {signUpFormError.password === "Invalid"
                                            && <BsExclamationTriangleFill size={16} className="errorImage" />}
                                        {getErrorMessage('SIGNUP', 'passwordMustContain')}
                                    </p>
                                    <ul>
                                        <li
                                            className={
                                                passwordErrorClasses.minLengthPasswordRuleClass
                                            }
                                        >
                                            {getErrorMessage('SIGNUP', 'passwordMinLength')}
                                        </li>
                                        <li
                                            className={
                                                passwordErrorClasses.hasNumberPasswordRuleClass
                                            }
                                        >
                                            {getErrorMessage('SIGNUP', 'passwordMinNumbers')}
                                        </li>
                                        <li
                                            className={
                                                passwordErrorClasses.hasUpperCasePasswordRuleClass
                                            }
                                        >
                                            {getErrorMessage('SIGNUP', 'passwordUpperCase')}
                                        </li>
                                    </ul>
                                </Col>
                            )}
                        </Row>
                        <FormGroup className="mb-3" controlId="formBasicConfirmPassword">
                            <FormLabel className="font-main">
                            {getLabel('SIGNUP', 'confirmPassword')}
                                <span className="label-required">*</span>
                            </FormLabel>
                            <FormControl
                                aria-required
                                type="password"
                                name="confirmPassword"
                                value={signUpForm.confirmPassword}
                                onChange={handleChange}
                                onBlur={() => validateFields("confirmPassword")}
                                isInvalid={(signUpFormError.confirmPassword !== "")}
                                className="checkout-input"
                                aria-label="Confirm Password"
                                placeholder="Confirm Password"
                                autoComplete="off"
                            />
                            <FormControl.Feedback type="invalid">
                                <BsExclamationTriangleFill size={16} className="errorImage" />
                                <span className="errorText font-sub">{signUpFormError.confirmPassword}</span>
                            </FormControl.Feedback>
                        </FormGroup>
                        <p className="font-sub text-center my-4">*Required Field</p>
                        <Form.Check
                            id="isSpecialOfferChecked"
                            type="checkbox"
                            label={getLabelByNotificationType(NotificationType.EMAIL)}
                            name="isSpecialOfferChecked"
                            checked={signUpForm.isSpecialOfferChecked}
                            onChange={handleCheckChange}
                            data-testid="isSpecialOfferChecked"
                        />
                        <Form.Check
                            id="isCateringChecked"
                            type="checkbox"
                            label={getLabelByNotificationType(NotificationType.EMAIL_CATERING)}
                            name="isCateringChecked"
                            checked={signUpForm.isCateringChecked}
                            onChange={handleCheckChange}
                            data-testid="isCateringChecked"
                        />
                        {isSMSEnabled && (
                            <Form.Check
                                id="isSmsChecked"
                                type="checkbox"
                                label={getLabelByNotificationType(NotificationType.SMS)}
                                name="isSmsChecked"
                                checked={!!signUpForm.isSmsChecked}
                                onChange={handleCheckChange}
                                data-testid="isSmsChecked"
                            />
                        )}
                       {privacyAndTermsText}
                        <p className="mt-3 text-center font-main">{getLabel('SIGNUP', 'signUpReadTerms')}</p>
                        <p className="mt-3 text-center font-sub f-w-100">{getLabel('SIGNUP', 'signUpReadPrivacy')}</p>

                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default SignUpDetails;
